import { ChangeEvent, FC, FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaginationBar from '../../components/PaginationBar';
import {
  VehicleBrand,
  createVehicleBrand,
  deleteVehicleBrand,
  fetchVehicleBrands,
  updateVehicleBrand,
} from '../../api/vehicleBrands';

interface BrandsState {
  records: VehicleBrand[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const formatDate = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
};

const VehicleBrandsPanel: FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [state, setState] = useState<BrandsState>({
    records: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [newBrandName, setNewBrandName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [updating, setUpdating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const triggerRefresh = () => setRefreshCounter((prev) => prev + 1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setListError(null);

    fetchVehicleBrands({
      page,
      limit: state.limit,
      search: debouncedSearch || undefined,
    })
      .then((response) => {
        if (!isMounted) {
          return;
        }

        const {
          data,
          meta: { total, page: currentPage, limit, totalPages },
        } = response;

        setState({
          records: data,
          total,
          page: currentPage,
          limit,
          totalPages,
        });
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to load vehicle brands.';
        setListError(message);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [page, refreshCounter, state.limit, debouncedSearch]);

  const totalPages = useMemo(() => Math.max(1, state.totalPages || 1), [state.totalPages]);

  const handleNewBrandNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewBrandName(event.target.value);
  };

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (creating) {
      return;
    }

    const trimmed = newBrandName.trim();
    if (!trimmed) {
      setCreateError('Please enter a brand name.');
      return;
    }

    setCreateError(null);
    setCreating(true);

    try {
      await createVehicleBrand({ name: trimmed });
      setNewBrandName('');
      setPage(1);
      triggerRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create vehicle brand.';
      setCreateError(message);
    } finally {
      setCreating(false);
    }
  };

  const startEditing = (brand: VehicleBrand) => {
    setEditingBrandId(brand.id);
    setEditName(brand.name);
    setEditError(null);
  };

  const cancelEditing = () => {
    setEditingBrandId(null);
    setEditName('');
    setEditError(null);
  };

  const handleUpdate = async () => {
    if (!editingBrandId || updating) {
      return;
    }

    const trimmed = editName.trim();
    if (!trimmed) {
      setEditError('Please enter a brand name.');
      return;
    }

    setEditError(null);
    setUpdating(true);

    try {
      await updateVehicleBrand(editingBrandId, { name: trimmed });
      cancelEditing();
      triggerRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update vehicle brand.';
      setEditError(message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (brand: VehicleBrand) => {
    if (deletingId || !window.confirm(`Delete vehicle brand "${brand.name}"?`)) {
      return;
    }

    setDeletingId(brand.id);
    setActionError(null);

    try {
      await deleteVehicleBrand(brand.id);
      if (state.records.length <= 1 && state.page > 1) {
        setPage((prev) => Math.max(1, prev - 1));
      } else {
        triggerRefresh();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete vehicle brand.';
      setActionError(message);
    } finally {
      setDeletingId(null);
    }
  };

  const summaryLabel = useMemo(() => {
    if (!state.total) {
      return 'No vehicle brands found';
    }

    const start = (state.page - 1) * state.limit;
    const end = start + state.records.length;
    return `Showing ${end} of ${state.total} vehicle brands`;
  }, [state.limit, state.page, state.records.length, state.total]);

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
      <input
        type="search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search brands..."
        className="mb-4 w-full sm:w-80 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
      />

      <form
        className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 shadow-sm"
        onSubmit={handleCreate}
      >

        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="text"
            value={newBrandName}
            onChange={handleNewBrandNameChange}
            placeholder="e.g. Toyota"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-80"
            disabled={creating}
          />
          <button
            type="submit"
            className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={creating}
          >
            {creating ? 'Saving…' : 'Add'}
          </button>
        </div>
        {createError ? (
          <p className="mt-2 text-sm text-danger">{createError}</p>
        ) : null}
      </form>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Related products</th>
              <th className="px-5 py-3 font-semibold">Updated</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={4}>
                  Loading...
                </td>
              </tr>
            ) : listError ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={4}>
                  Unable to load brands. Please try again.
                </td>
              </tr>
            ) : state.records.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={4}>
                  No brands found.
                </td>
              </tr>
            ) : (
              state.records.map((brand) => {
                const isEditing = brand.id === editingBrandId;
                const relatedCount = brand.relatedVariantCount ?? 0;

                return (
                  <tr key={brand.id} className="even:bg-slate-50/60">
                    <td className="px-5 py-3 text-slate-700">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={(event) => setEditName(event.target.value)}
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                          disabled={updating}
                        />
                      ) : (
                        brand.name
                      )}
                      {isEditing && editError ? (
                        <p className="mt-1 text-xs text-danger">{editError}</p>
                      ) : null}
                    </td>
                    <td className="px-5 py-3 text-slate-500">
                      {relatedCount}
                    </td>
                    <td className="px-5 py-3 text-slate-500">{formatDate(brand.updatedAt)}</td>
                    <td className="px-5 py-3 text-right">
                      {isEditing ? (
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
                            onClick={handleUpdate}
                            disabled={updating}
                          >
                            {updating ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-70"
                            onClick={cancelEditing}
                            disabled={updating}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                            onClick={() => startEditing(brand)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                            onClick={() => navigate(`/vehicle-management/brands/${brand.id}/related-products`)}
                          >
                            Related products
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg border border-danger/40 px-3 py-1.5 text-xs font-semibold text-danger transition hover:bg-danger/10 disabled:cursor-not-allowed disabled:opacity-60"
                            onClick={() => handleDelete(brand)}
                            disabled={deletingId === brand.id}
                          >
                            {deletingId === brand.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={state.page}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={<span>{summaryLabel}</span>}
        renderExtra={
          <span className="text-xs text-slate-400">Page {state.page} of {totalPages} ({state.limit} / page)</span>
        }
      />

      {actionError ? (
        <p className="mt-4 text-sm text-danger">{actionError}</p>
      ) : null}
    </div>
  );
};

export default VehicleBrandsPanel;
